"use client";

import { motion } from "framer-motion";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import type { IconType } from "react-icons";

interface ErrorLayoutProps {
  icon: IconType;
  tagline: string;
  title: string;
  description: string;
  actions: React.ReactNode;
  fullScreen?: boolean; // For global error without navbar
  debugInfo?: string;
}

export default function ErrorLayout({
  icon: Icon,
  tagline,
  title,
  description,
  actions,
  fullScreen = false,
  debugInfo,
}: ErrorLayoutProps) {
  return (
    <main className="min-h-screen bg-brand-charcoal flex flex-col text-white font-sans selection:bg-brand-gold selection:text-white relative z-50">
      {!fullScreen && <Navbar />}

      <section className="flex-grow flex items-center justify-center py-20 relative overflow-hidden">
        {/* Background Luxury Gradients */}
        <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-brand-gold/5 rounded-full mix-blend-overlay filter blur-[120px] pointer-events-none" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-white/5 rounded-full mix-blend-overlay filter blur-[100px] pointer-events-none" />

        <div className="container mx-auto px-6 text-center max-w-lg relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="w-24 h-24 bg-gradient-to-br from-white/10 to-transparent border border-white/10 rounded-full flex items-center justify-center mx-auto mb-8 text-brand-gold shadow-2xl backdrop-blur-sm relative"
          >
            <div className="absolute inset-0 bg-brand-gold/20 blur-xl rounded-full opacity-50" />
            <Icon size={40} className="relative z-10" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6, ease: "easeOut" }}
          >
            <span className="text-brand-gold text-xs font-bold tracking-[0.3em] uppercase block mb-4">
              {tagline}
            </span>

            <h1 className="font-serif text-4xl md:text-5xl text-white mb-6 leading-tight">
              {title}
            </h1>

            <p className="text-gray-400 font-light text-lg mb-10 leading-relaxed max-w-md mx-auto">
              {description}
            </p>

            <div className="flex flex-col md:flex-row gap-4 justify-center items-center w-full">
              {actions}
            </div>

            {debugInfo && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-12 p-4 bg-black/50 border border-white/10 rounded text-left overflow-auto max-h-40 w-full max-w-md mx-auto text-xs text-red-400 font-mono"
              >
                {debugInfo}
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {!fullScreen && <Footer />}
    </main>
  );
}
